import { useEffect, useMemo, useRef, useState, type ChangeEvent, type Dispatch, type SetStateAction } from "react";

type Language = "en" | "kh";
type GuestStatus = "Joining" | "Maybe" | "Not yet";
type GuestGroup = "ភ្ញៀវកូនស្រី" | "ភ្ញៀវកូនប្រុស";
type AttendanceFilter = "All" | GuestStatus;
type AttendanceGroupFilter = "All" | GuestGroup;
type AttendanceDoneFilter = "All" | "Pending" | "Done";
type AttendanceInviteFilter = "All" | "Invited" | "Not invited";
type AttendanceGiftFilter = "All" | "Gift" | "No gift";
type AttendanceHouseholdFilter = "all" | "single" | "family";
type AttendanceCategoryFilter = "all" | string;
type GiftCurrency = "USD" | "Riel";
type UploadGuestMode = "all" | "groom" | "bride";
type GuestImportPreviewRow = {
  name: string;
  group: GuestGroup | null;
  hasGift: boolean;
  giftAmount: number | null;
  giftCurrency: GiftCurrency;
};
type CategoryItem = {
  id: number;
  name: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
};
type GuestEstimate = {
  totalFound: number;
  brideTotal: number;
  groomTotal: number;
  estimatedGuestTotal: number;
  guestsPerTable: number;
  estimatedTableCount: number;
};

type Guest = {
  id: number;
  name: string;
  phone: string;
  status: GuestStatus;
  group: GuestGroup;
  category: string;
  notes: string;
  latitude: number | null;
  longitude: number | null;
  source?: "api" | "local";
  giftAmount?: number | null;
  giftCurrency?: GiftCurrency;
  invited?: boolean;
  done?: boolean;
};

type GuestsText = {
  tab: string;
  title: string;
  countSuffix: string;
  searchPlaceholder: string;
  searchInputLabel: string;
  estimateLabel: string;
  all: string;
  groupFilter: {
    all: string;
    bride: string;
    groom: string;
  };
  status: Record<GuestStatus, string>;
  doneFilter: Record<AttendanceDoneFilter, string>;
  invitedFilter: Record<AttendanceInviteFilter, string>;
  giftFilter: Record<AttendanceGiftFilter, string>;
  table: {
    no: string;
    name: string;
    contact: string;
    category: string;
    status: string;
    group: string;
    latitude: string;
    longitude: string;
    gift: string;
    invite: string;
    action: string;
  };
  noGuests: string;
  doneBtn: string;
  markDoneBtn: string;
  inviteBtn: string;
  invitedBtn: string;
  totalUsd: string;
  totalRiel: string;
};

type GuestsSectionProps = {
  guestsText: Record<Language, GuestsText>;
  language: Language;
  attendanceRows: Guest[];
  totalGuestCount: number;
  withGiftGuestCount: number;
  attendanceGroupCounts: {
    all: number;
    bride: number;
    groom: number;
  };
  attendanceGroupFilter: AttendanceGroupFilter;
  setAttendanceGroupFilter: Dispatch<SetStateAction<AttendanceGroupFilter>>;
  attendanceFilter: AttendanceFilter;
  attendanceDoneFilter: AttendanceDoneFilter;
  setAttendanceDoneFilter: Dispatch<SetStateAction<AttendanceDoneFilter>>;
  attendanceInviteFilter: AttendanceInviteFilter;
  setAttendanceInviteFilter: Dispatch<SetStateAction<AttendanceInviteFilter>>;
  attendanceGiftFilter: AttendanceGiftFilter;
  setAttendanceGiftFilter: Dispatch<SetStateAction<AttendanceGiftFilter>>;
  attendanceHouseholdFilter: AttendanceHouseholdFilter;
  setAttendanceHouseholdFilter: Dispatch<SetStateAction<AttendanceHouseholdFilter>>;
  categoryItems: CategoryItem[];
  attendanceCategoryFilter: AttendanceCategoryFilter;
  setAttendanceCategoryFilter: Dispatch<SetStateAction<AttendanceCategoryFilter>>;
  attendanceSearch: string;
  onAttendanceSearchChange: (value: string) => void;
  handleDownloadAllGuestsExcel: () => void;
  isDownloadingAllGuests: boolean;
  onLoadGuestsWithGift: () => Promise<Guest[]>;
  onShowSameNameHaveGift: () => Promise<void> | void;
  onShowAllGuests: () => void;
  onDownloadGuestsWithGiftExcel: () => Promise<void>;
  isLoadingGuestsWithGift: boolean;
  isDownloadingGuestsWithGift: boolean;
  isShowingSameNameHaveGift: boolean;
  sameNameWithGiftCount: number;
  onToggleInvite: (guest: Guest) => void;
  onToggleDone: (guest: Guest) => void;
  doneById: (guestId: number) => boolean;
  openGiftModal: (guest: Guest) => void;
  attendanceGiftTotals: {
    usd: number;
    riel: number;
  };
  formatCurrency: (value: number) => string;
  onDeleteSelected: (ids: number[]) => Promise<boolean> | boolean;
  isDeletingSelected: boolean;
  guestEstimate: GuestEstimate | null;
  isLoadingGuestEstimate: boolean;
  guestEstimateError: string;
  onRequestGuestEstimate: () => void;
  categoryTranslations: Record<string, string>;
  onCreateCategory: (nameEn: string, nameKh?: string) => Promise<void>;
  onUpdateCategory: (id: number, nameEn: string, nameKh?: string) => Promise<void>;
  onDeleteCategory: (id: number) => Promise<void>;
  isLoadingAttendanceGuests: boolean;
  attendancePage: number;
  attendanceTotalPages: number;
  onAttendancePageChange: (page: number) => void;
  onDownloadGuestImportSample: () => void;
  onPreviewGuestExcel: (
    file: File,
    mode: UploadGuestMode,
  ) => Promise<GuestImportPreviewRow[]>;
  onUploadGuestExcel: (
    file: File,
    mode: UploadGuestMode,
  ) => Promise<{ created: number; failed: number; message?: string }>;
  isUploadingGuestExcel: boolean;
};

export default function GuestsSection({
  guestsText,
  language,
  attendanceRows,
  totalGuestCount,
  withGiftGuestCount,
  attendanceGroupCounts,
  attendanceGroupFilter,
  setAttendanceGroupFilter,
  attendanceFilter,
  attendanceDoneFilter,
  setAttendanceDoneFilter,
  attendanceInviteFilter,
  setAttendanceInviteFilter,
  attendanceGiftFilter,
  setAttendanceGiftFilter,
  attendanceHouseholdFilter,
  setAttendanceHouseholdFilter,
  categoryItems,
  attendanceCategoryFilter,
  setAttendanceCategoryFilter,
  attendanceSearch,
  onAttendanceSearchChange,
  handleDownloadAllGuestsExcel,
  isDownloadingAllGuests,
  onLoadGuestsWithGift,
  onShowSameNameHaveGift,
  onShowAllGuests,
  onDownloadGuestsWithGiftExcel,
  isLoadingGuestsWithGift,
  isDownloadingGuestsWithGift,
  isShowingSameNameHaveGift,
  sameNameWithGiftCount,
  onToggleInvite,
  onToggleDone,
  doneById,
  openGiftModal,
  attendanceGiftTotals,
  formatCurrency,
  onDeleteSelected,
  isDeletingSelected,
  guestEstimate,
  isLoadingGuestEstimate,
  guestEstimateError,
  onRequestGuestEstimate,
  categoryTranslations,
  onCreateCategory,
  onUpdateCategory,
  onDeleteCategory,
  isLoadingAttendanceGuests,
  attendancePage,
  attendanceTotalPages,
  onAttendancePageChange,
  onDownloadGuestImportSample,
  onPreviewGuestExcel,
  onUploadGuestExcel,
  isUploadingGuestExcel,
}: GuestsSectionProps) {
  const isKh = language === "kh";
  const [selectedGuestIds, setSelectedGuestIds] = useState<number[]>([]);
  const [showEstimateModal, setShowEstimateModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showGuestImportModal, setShowGuestImportModal] = useState(false);
  const [newCategoryNameEn, setNewCategoryNameEn] = useState("");
  const [newCategoryNameKh, setNewCategoryNameKh] = useState("");
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(null);
  const [editingCategoryNameEn, setEditingCategoryNameEn] = useState("");
  const [editingCategoryNameKh, setEditingCategoryNameKh] = useState("");
  const [isCategorySaving, setIsCategorySaving] = useState(false);
  const [categoryError, setCategoryError] = useState("");
  const [isAttendanceSearchFocused, setIsAttendanceSearchFocused] = useState(false);
  const [guestImportFeedback, setGuestImportFeedback] = useState<{
    tone: "success" | "warning" | "error";
    message: string;
  } | null>(null);
  const [guestImportFile, setGuestImportFile] = useState<File | null>(null);
  const [guestImportPreviewRows, setGuestImportPreviewRows] = useState<GuestImportPreviewRow[]>([]);
  const [isLoadingGuestImportPreview, setIsLoadingGuestImportPreview] = useState(false);
  const [guestImportPreviewSearch, setGuestImportPreviewSearch] = useState("");
  const [showGuestsWithGiftOnly, setShowGuestsWithGiftOnly] = useState(false);
  const [guestsWithGiftRows, setGuestsWithGiftRows] = useState<Guest[]>([]);
  const guestImportInputRef = useRef<HTMLInputElement | null>(null);
  const guestImportModeRef = useRef<UploadGuestMode>("all");
  const filterGroupClass = isKh ? "h-10 gap-1 p-1" : "h-10 gap-1.25 p-1";
  const filterChipClass = isKh
    ? "flex h-full items-center px-2.5 text-[11px]"
    : "flex h-full items-center px-2.5 text-xs";
  const dropdownTextClass = isKh ? "text-[11px]" : "text-xs";
  const localizeCategory = (category: string) => {
    if (!isKh) return category;
    const normalized = category.trim().toLowerCase();
    const mapped = categoryTranslations[normalized];
    if (mapped?.trim()) return mapped.trim();
    if (normalized === "friend" || normalized === "friends") return "មិត្តភក្តិ";
    if (normalized === "colleague" || normalized === "colleagues") return "មិត្តរួមការងារ";
    if (normalized === "management") return "ថ្នាក់គ្រប់គ្រង";
    if (normalized === "relative" || normalized === "relatives") return "សាច់ញាតិ";
    if (normalized === "sibling" || normalized === "siblings") return "បងប្អូន";
    if (normalized === "friends of parents") return "មិត្តភក្តិរបស់ឪពុកម្តាយ";
    if (normalized === "sibling's friend" || normalized === "siblings friend") {
      return "មិត្តភក្តិបងប្អូន";
    }
    return category;
  };
  const householdFilterLabel = (household: AttendanceHouseholdFilter) => {
    if (language !== "kh") {
      if (household === "all") return "All";
      if (household === "single") return "Guest single";
      return "Guest family";
    }
    if (household === "all") return "ទាំងអស់";
    if (household === "single") return "ភ្ញៀវម្នាក់ឯង";
    return "ភ្ញៀវគ្រួសារ";
  };
  const toCategoryQueryValue = (categoryName: string) => categoryName.trim().toLowerCase();
  const toApiGuestGroupValue = (group: GuestGroup) =>
    group === "ភ្ញៀវកូនស្រី" ? "bride" : "groom";
  const categoryFilterLabel = (categoryName: string) =>
    language === "kh" ? localizeCategory(categoryName) : categoryName;
  const uniqueCategoryOptions = useMemo(() => {
    const seen = new Set<string>();
    return categoryItems.map((item) => item.name).filter((categoryName) => {
      const key = toCategoryQueryValue(categoryName);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [categoryItems]);
  const baseAttendanceRows = isShowingSameNameHaveGift
    ? attendanceRows
    : showGuestsWithGiftOnly
      ? guestsWithGiftRows
      : attendanceRows;
  const visibleAttendanceRows = useMemo(() => {
    if (attendanceGroupFilter === "ភ្ញៀវកូនប្រុស") {
      return baseAttendanceRows.filter((guest) => toApiGuestGroupValue(guest.group) === "groom");
    }
    if (attendanceGroupFilter === "ភ្ញៀវកូនស្រី") {
      return baseAttendanceRows.filter((guest) => toApiGuestGroupValue(guest.group) === "bride");
    }
    return baseAttendanceRows;
  }, [attendanceGroupFilter, baseAttendanceRows]);
  const paginatedAttendanceRows = useMemo(() => {
    if (attendanceGroupFilter !== "All") {
      return visibleAttendanceRows;
    }
    const start = (attendancePage - 1) * 150;
    return visibleAttendanceRows.slice(start, start + 150);
  }, [attendanceGroupFilter, attendancePage, visibleAttendanceRows]);
  const selectedVisibleGuestIds = useMemo(
    () => selectedGuestIds.filter((id) => paginatedAttendanceRows.some((guest) => guest.id === id)),
    [selectedGuestIds, paginatedAttendanceRows],
  );
  const allVisibleSelected = useMemo(
    () =>
      paginatedAttendanceRows.length > 0 &&
      selectedVisibleGuestIds.length === paginatedAttendanceRows.length,
    [selectedVisibleGuestIds.length, paginatedAttendanceRows.length],
  );
  const estimateButtonLabel = isKh ? "ប៉ាន់ស្មានភ្ញៀវ" : "Estimate guests";
  const estimateCountLabel = isKh ? "ចំនួនភ្ញៀវប៉ាន់ស្មាន" : "Estimated guests";
  const estimateTableRangeLabel = isKh ? "ចំនួនតុប៉ាន់ស្មាន" : "Estimated tables";
  const brideGuestTotalLabel = isKh ? "ភ្ញៀវខាងកូនស្រី" : "Bride guests";
  const groomGuestTotalLabel = isKh ? "ភ្ញៀវខាងកូនប្រុស" : "Groom guests";
  const totalGuestsLabel = isKh ? "ភ្ញៀវសរុប" : "Total guests";
  const withGiftCountLabel = isKh ? "ភ្ញៀវមានចំណងដៃ" : "Guests with gift";
  const totalGiftAmountLabel = isKh ? "ចំណងដៃសរុប" : "Total gift amount";
  const brideGuestCount = attendanceGroupCounts.bride;
  const groomGuestCount = attendanceGroupCounts.groom;
  const attendanceFilterTabs: Array<{
    key: AttendanceGroupFilter;
    label: string;
    count: number;
  }> = [
    {
      key: "All",
      label: guestsText[language].groupFilter.all,
      count: attendanceGroupCounts.all,
    },
    {
      key: "ភ្ញៀវកូនប្រុស",
      label: guestsText[language].groupFilter.groom,
      count: attendanceGroupCounts.groom,
    },
    {
      key: "ភ្ញៀវកូនស្រី",
      label: guestsText[language].groupFilter.bride,
      count: attendanceGroupCounts.bride,
    },
  ];
  const visibleAttendanceGiftTotals = useMemo(() => {
    if (!showGuestsWithGiftOnly && !isShowingSameNameHaveGift) {
      return attendanceGiftTotals;
    }

    return visibleAttendanceRows.reduce(
      (totals, guest) => {
        if ((guest.giftAmount ?? 0) <= 0) {
          return totals;
        }
        if (guest.giftCurrency === "Riel") {
          totals.riel += guest.giftAmount ?? 0;
        } else {
          totals.usd += guest.giftAmount ?? 0;
        }
        return totals;
      },
      { usd: 0, riel: 0 },
    );
  }, [attendanceGiftTotals, showGuestsWithGiftOnly, isShowingSameNameHaveGift, visibleAttendanceRows]);
  const visibleTotalGuestCount = isShowingSameNameHaveGift
    ? sameNameWithGiftCount
    : showGuestsWithGiftOnly
      ? visibleAttendanceRows.length
      : attendanceGroupCounts.all;
  const visibleWithGiftGuestCount = isShowingSameNameHaveGift
    ? sameNameWithGiftCount
    : showGuestsWithGiftOnly
      ? visibleAttendanceRows.length
      : withGiftGuestCount;
  const closeLabel = isKh ? "បិទ" : "Close";
  const doneFilterLabel = isKh ? "បានធ្វើ" : "Done";
  const inviteFilterLabel = isKh ? "អញ្ជើញ" : "Invite";
  const giftFilterLabel = isKh ? "ចំណងដៃ" : "Gift";
  const categoryFilterTitle = isKh ? "ប្រភេទ" : "Category";
  const householdFilterTitle = isKh ? "ប្រភេទភ្ញៀវ" : "Household";
  const groupFilterTitle = isKh ? "ក្រុមភ្ញៀវ" : "Guest group";
  const deleteFilterTitle = isKh ? "លុប" : "Delete";
  const downloadAllFilterTitle = isKh ? "ទាញយកទាំងអស់" : "Download all";
  const uploadExcelTitle = isKh ? "អាប់ឡូត Excel" : "Upload Excel";
  const uploadGuestsLabel = isKh ? "ភ្ញៀវ" : "Guests";
  const uploadExcelHint = isKh
    ? "អាច upload បានទាំង group guest = ស្រី/ប្រុស ឬ bride/groom។ ប្រុស = groom, ស្រី = bride។ Gift អាចទទេបាន។ បើមាន gift តែគ្មាន currency នឹងកំណត់ជា Riel ស្វ័យប្រវត្តិ។"
    : "You can upload group guest as ស្រី/ប្រុស or bride/groom. `ប្រុស` maps to groom, `ស្រី` maps to bride. Gift can be empty. If a gift amount has no currency, it will default to Riel.";
  const uploadGuestModalTitle = isKh ? "អាប់ឡូតភ្ញៀវ Excel" : "Upload guest Excel";
  const downloadSampleLabel = isKh ? "ទាញយក Sample file" : "Download sample file";
  const chooseFileLabel = isKh ? "ជ្រើសឯកសារ" : "Choose file";
  const uploadLabel = isKh ? "អាប់ឡូត" : "Upload";
  const noFileSelectedLabel = isKh ? "មិនទាន់ជ្រើសឯកសារ" : "No file selected";
  const downloadGiftListLabel = isKh ? "ទាញយកមានចំណងដៃ" : "Download gift list";
  const filterLabelTextClass =
    "text-[10px] font-semibold uppercase tracking-wider text-[color:var(--muted)]";
  const categoryManageLabel = isKh ? "បន្ថែម/គ្រប់គ្រងប្រភេទ" : "Add category...";
  const isEnglishCategoryName = (value: string) => /^[A-Za-z0-9 '&-]+$/.test(value.trim());
  const estimatedTableRangeText = useMemo(
    () => String(Math.max(0, guestEstimate?.estimatedTableCount ?? 0)),
    [guestEstimate],
  );
  const openGuestImportModal = (mode: UploadGuestMode) => {
    guestImportModeRef.current = mode;
    setGuestImportFile(null);
    setGuestImportPreviewRows([]);
    setGuestImportPreviewSearch("");
    setShowGuestImportModal(true);
  };
  const handleGuestImportChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setGuestImportFile(file);
    setGuestImportFeedback(null);
    setIsLoadingGuestImportPreview(true);
    try {
      const previewRows = await onPreviewGuestExcel(file, guestImportModeRef.current);
      setGuestImportPreviewRows(previewRows);
    } catch (error) {
      setGuestImportPreviewRows([]);
      setGuestImportFeedback({
        tone: "error",
        message: error instanceof Error ? error.message : "Unable to preview guest file.",
      });
    } finally {
      setIsLoadingGuestImportPreview(false);
    }
  };
  const handleGuestImportSubmit = async () => {
    if (!guestImportFile) {
      setGuestImportFeedback({
        tone: "error",
        message: isKh ? "សូមជ្រើសឯកសារ Excel ជាមុនសិន។" : "Please choose an Excel file first.",
      });
      return;
    }
    try {
      const result = await onUploadGuestExcel(guestImportFile, guestImportModeRef.current);
      setGuestImportFeedback({
        tone: result.failed > 0 ? "warning" : "success",
        message:
          result.message ??
          (result.failed > 0
            ? `${result.created} uploaded, ${result.failed} failed.`
            : `${result.created} guests uploaded successfully.`),
      });
    } catch (error) {
      setGuestImportFeedback({
        tone: "error",
        message: error instanceof Error ? error.message : "Unable to upload guest file.",
      });
      return;
    }
    setGuestImportFile(null);
  };
  const handleDeleteSelectedGuests = async () => {
    if (selectedVisibleGuestIds.length === 0 || isDeletingSelected) {
      return;
    }

    const deletedIds = [...selectedVisibleGuestIds];
    const didDelete = await onDeleteSelected(deletedIds);
    if (!didDelete) {
      return;
    }

    const deletedIdSet = new Set(deletedIds);
    setSelectedGuestIds((prev) => prev.filter((id) => !deletedIdSet.has(id)));
    setGuestsWithGiftRows((prev) => prev.filter((guest) => !deletedIdSet.has(guest.id)));
  };
  useEffect(() => {
    setShowGuestsWithGiftOnly(false);
    setGuestsWithGiftRows([]);
    setSelectedGuestIds([]);
  }, [
    attendanceCategoryFilter,
    attendanceDoneFilter,
    attendanceFilter,
    attendanceGiftFilter,
    attendanceGroupFilter,
    attendanceHouseholdFilter,
    attendanceInviteFilter,
    attendanceSearch,
  ]);
  const filteredGuestImportPreviewRows = useMemo(() => {
    const query = guestImportPreviewSearch.trim().toLowerCase();
    if (!query) return guestImportPreviewRows;
    return guestImportPreviewRows.filter((row) => {
      const groupLabel =
        row.group == null ? "" : row.group === "ភ្ញៀវកូនស្រី" ? "bride ស្រី" : "groom ប្រុស";
      const amountLabel = row.hasGift && row.giftAmount != null ? String(row.giftAmount) : "";
      const currencyLabel = row.hasGift ? row.giftCurrency : "";
      return [row.name, groupLabel, amountLabel, currencyLabel].some((value) =>
        value.toLowerCase().includes(query),
      );
    });
  }, [guestImportPreviewRows, guestImportPreviewSearch]);

  return (
    <div className="mt-2 min-h-[calc(100svh-4.75rem)] sm:mt-4 sm:h-full sm:min-h-0">
      <div className="flex h-full min-h-0 flex-col overflow-visible rounded-3xl border border-[color:var(--panel-border)] bg-[color:var(--card)] p-4 shadow-[var(--panel-shadow)] sm:overflow-hidden sm:p-7">
        <div className="shrink-0">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-[family-name:var(--font-display)] text-2xl text-[color:var(--foreground)]">
                  {guestsText[language].title}
                </h3>
                <div className="flex items-center gap-2 sm:hidden">
                  <button
                    type="button"
                    onClick={handleDownloadAllGuestsExcel}
                    data-tab-trigger="true"
                    disabled={isDownloadingAllGuests || totalGuestCount === 0}
                    className={`flex h-9 min-w-9 items-center justify-center rounded-full border px-2 transition ${
                      isDownloadingAllGuests || totalGuestCount === 0
                        ? "cursor-not-allowed border-[color:var(--field-border)] bg-[color:var(--surface)] text-[color:var(--muted)] opacity-60"
                        : "border-[color:var(--field-border)] bg-[color:var(--surface)] text-[color:var(--foreground)] hover:bg-[color:var(--surface-hover)]"
                    }`}
                    aria-label="Download all guests"
                    title="Download all guests"
                  >
                    {isDownloadingAllGuests ? (
                      <svg viewBox="0 0 24 24" className="h-4 w-4 animate-spin" aria-hidden="true">
                        <circle
                          cx="12"
                          cy="12"
                          r="9"
                          fill="none"
                          stroke="currentColor"
                          strokeOpacity="0.3"
                          strokeWidth="2"
                        />
                        <path
                          d="M21 12a9 9 0 0 0-9-9"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    ) : (
                      <svg
                        viewBox="0 0 24 24"
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M12 3v12" />
                        <path d="m7 10 5 5 5-5" />
                        <path d="M4 19h16" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <p className="mt-1 text-sm text-[color:var(--muted)]">
                {attendanceGroupCounts.all} {guestsText[language].countSuffix}
              </p>
            </div>
            <div className="flex w-full flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
              <div className="flex flex-wrap items-end gap-2">
                <label className="grid gap-1 text-[10px] font-semibold uppercase tracking-wider text-[color:var(--muted)]">
                  {groupFilterTitle}
                  <div className="flex max-w-full flex-nowrap gap-1.5 overflow-x-auto rounded-full border border-[color:var(--field-border)] bg-[color:var(--surface)] p-1.5">
                    {attendanceFilterTabs.map((tab) => {
                      const isActive = attendanceGroupFilter === tab.key;
                      return (
                        <button
                          key={tab.key}
                          type="button"
                          data-tab-trigger="true"
                          onClick={() => {
                            setSelectedGuestIds([]);
                            setAttendanceGroupFilter(tab.key);
                          }}
                          className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                            isActive
                              ? "bg-[color:var(--accent)] text-white"
                              : "text-[color:var(--foreground)] hover:bg-[color:var(--surface-hover)]"
                          }`}
                        >
                          {tab.label} ({tab.count})
                        </button>
                      );
                    })}
                  </div>
                </label>
                <label className="grid gap-1 text-[10px] font-semibold uppercase tracking-wider text-[color:var(--muted)]">
                  {doneFilterLabel}
                  <select
                    value={attendanceDoneFilter}
                    onChange={(event) =>
                      setAttendanceDoneFilter(event.target.value as AttendanceDoneFilter)
                    }
                    className={`h-10 w-[6rem] rounded-full border border-[color:var(--field-border)] bg-[color:var(--surface)] px-2.5 font-semibold text-[color:var(--foreground)] outline-none transition focus:border-[color:var(--accent)] ${dropdownTextClass}`}
                    aria-label="Done filter"
                  >
                    <option value="All">{guestsText[language].doneFilter.All}</option>
                    <option value="Pending">{guestsText[language].doneFilter.Pending}</option>
                    <option value="Done">{guestsText[language].doneFilter.Done}</option>
                  </select>
                </label>
                <label className="grid gap-1 text-[10px] font-semibold uppercase tracking-wider text-[color:var(--muted)]">
                  {inviteFilterLabel}
                  <select
                    value={attendanceInviteFilter}
                    onChange={(event) =>
                      setAttendanceInviteFilter(event.target.value as AttendanceInviteFilter)
                    }
                    className={`h-10 w-[6rem] rounded-full border border-[color:var(--field-border)] bg-[color:var(--surface)] px-2.5 font-semibold text-[color:var(--foreground)] outline-none transition focus:border-[color:var(--accent)] ${dropdownTextClass}`}
                    aria-label="Invite filter"
                  >
                    <option value="All">{guestsText[language].invitedFilter.All}</option>
                    <option value="Invited">{guestsText[language].invitedFilter.Invited}</option>
                    <option value="Not invited">{guestsText[language].invitedFilter["Not invited"]}</option>
                  </select>
                </label>
                <label className="grid gap-1 text-[10px] font-semibold uppercase tracking-wider text-[color:var(--muted)]">
                  {giftFilterLabel}
                  <select
                    value={attendanceGiftFilter}
                    onChange={(event) =>
                      setAttendanceGiftFilter(event.target.value as AttendanceGiftFilter)
                    }
                    className={`h-10 w-[6.25rem] rounded-full border border-[color:var(--field-border)] bg-[color:var(--surface)] px-2.5 font-semibold text-[color:var(--foreground)] outline-none transition focus:border-[color:var(--accent)] ${dropdownTextClass}`}
                    aria-label="Gift filter"
                  >
                    <option value="All">{guestsText[language].giftFilter.All}</option>
                    <option value="Gift">{guestsText[language].giftFilter.Gift}</option>
                    <option value="No gift">{guestsText[language].giftFilter["No gift"]}</option>
                  </select>
                </label>
                <label className="grid gap-1 text-[10px] font-semibold uppercase tracking-wider text-[color:var(--muted)]">
                  {categoryFilterTitle}
                  <select
                    value={attendanceCategoryFilter}
                    onChange={(event) => {
                      const value = event.target.value;
                      if (value === "__manage__") {
                        setShowCategoryModal(true);
                        setCategoryError("");
                        return;
                      }
                      setAttendanceCategoryFilter(value as AttendanceCategoryFilter);
                    }}
                    className={`h-10 w-[8rem] rounded-full border border-[color:var(--field-border)] bg-[color:var(--surface)] px-2.5 font-semibold text-[color:var(--foreground)] outline-none transition focus:border-[color:var(--accent)] ${dropdownTextClass}`}
                    aria-label="Category filter"
                  >
                    <option value="all">{guestsText[language].all}</option>
                    {uniqueCategoryOptions.map((categoryName) => {
                      const optionValue = categoryName.trim();
                      return (
                        <option key={optionValue} value={optionValue}>
                          {categoryFilterLabel(categoryName)}
                        </option>
                      );
                    })}
                    <option value="__manage__">{categoryManageLabel}</option>
                  </select>
                </label>
                <label className="grid gap-1 text-[10px] font-semibold uppercase tracking-wider text-[color:var(--muted)]">
                  {householdFilterTitle}
                  <select
                    value={attendanceHouseholdFilter}
                    onChange={(event) =>
                      setAttendanceHouseholdFilter(event.target.value as AttendanceHouseholdFilter)
                    }
                    className={`h-10 w-[7rem] rounded-full border border-[color:var(--field-border)] bg-[color:var(--surface)] px-2.5 font-semibold text-[color:var(--foreground)] outline-none transition focus:border-[color:var(--accent)] ${dropdownTextClass}`}
                    aria-label="Household filter"
                  >
                    <option value="all">{householdFilterLabel("all")}</option>
                    <option value="single">{householdFilterLabel("single")}</option>
                    <option value="family">{householdFilterLabel("family")}</option>
                  </select>
                </label>
              </div>
              <div className="flex flex-wrap items-end justify-start gap-2 xl:justify-end">
                <div className="flex w-full flex-col gap-1 sm:w-[15rem] lg:w-[16rem]">
                  <div className="flex items-center justify-between">
                    <span className={filterLabelTextClass}>{guestsText[language].searchInputLabel}</span>
                    <span className={filterLabelTextClass}>{guestsText[language].estimateLabel}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative min-w-0 flex-1">
                      <input
                        value={attendanceSearch}
                        onChange={(event) => onAttendanceSearchChange(event.target.value)}
                        onFocus={() => setIsAttendanceSearchFocused(true)}
                        onBlur={() => setIsAttendanceSearchFocused(false)}
                        placeholder={guestsText[language].searchPlaceholder}
                        className="h-10 w-full rounded-full border border-[color:var(--field-border)] bg-[color:var(--surface)] px-4 pr-10 text-xs text-[color:var(--foreground)] outline-none transition focus:border-[color:var(--accent)]"
                      />
                      {(attendanceSearch.trim() || isAttendanceSearchFocused) && (
                        <button
                          type="button"
                          onClick={() => onAttendanceSearchChange("")}
                          className="absolute right-2 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full border border-slate-300/80 bg-white/90 text-slate-600 transition hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-900/80 dark:text-slate-200 dark:hover:bg-slate-800"
                          aria-label="Clear search"
                          title="Clear search"
                        >
                          <svg
                            viewBox="0 0 24 24"
                            className="h-3.5 w-3.5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M18 6 6 18" />
                            <path d="m6 6 12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setShowEstimateModal(true);
                        onRequestGuestEstimate();
                      }}
                      data-tab-trigger="true"
                      disabled={isLoadingGuestEstimate}
                      className={`flex h-10 w-10 items-center justify-center rounded-full border transition ${
                        isLoadingGuestEstimate
                          ? "border-[color:var(--field-border)] bg-[color:var(--surface-hover)] text-[color:var(--muted)]"
                          : "border-[color:var(--field-border)] bg-[color:var(--surface)] text-[color:var(--foreground)] hover:bg-[color:var(--surface-hover)]"
                      }`}
                      aria-label={estimateButtonLabel}
                      title={estimateButtonLabel}
                    >
                      {isLoadingGuestEstimate ? (
                        <svg viewBox="0 0 24 24" className="h-4 w-4 animate-spin" aria-hidden="true">
                          <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeOpacity="0.3" strokeWidth="2" />
                          <path
                            d="M21 12a9 9 0 0 0-9-9"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      ) : (
                        <svg
                          viewBox="0 0 24 24"
                          className="h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <circle cx="12" cy="12" r="3.5" />
                          <circle cx="12" cy="4.5" r="1.2" />
                          <circle cx="19.5" cy="12" r="1.2" />
                          <circle cx="12" cy="19.5" r="1.2" />
                          <circle cx="4.5" cy="12" r="1.2" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <span className={filterLabelTextClass}>{uploadExcelTitle}</span>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => openGuestImportModal("all")}
                      disabled={isUploadingGuestExcel}
                      className={`inline-flex h-10 min-w-10 items-center justify-center rounded-full px-4 text-xs font-semibold transition ${
                        isUploadingGuestExcel
                          ? "cursor-not-allowed bg-[color:var(--field-border)] text-[color:var(--muted)] opacity-70"
                          : "bg-[color:var(--accent)] !text-white hover:brightness-110 dark:!text-white"
                      }`}
                    >
                      {uploadGuestsLabel}
                    </button>
                  </div>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <span className={filterLabelTextClass}>{deleteFilterTitle}</span>
                  <button
                    type="button"
                    onClick={() => {
                      void handleDeleteSelectedGuests();
                    }}
                    disabled={selectedVisibleGuestIds.length === 0 || isDeletingSelected}
                    data-tab-trigger="true"
                    className={`flex h-10 min-w-10 items-center justify-center rounded-full px-3 transition ${
                      selectedVisibleGuestIds.length === 0 || isDeletingSelected
                        ? "cursor-not-allowed bg-[color:var(--field-border)] text-[color:var(--muted)] opacity-70"
                        : "bg-red-600 text-white hover:bg-red-700"
                    }`}
                    aria-label="Delete selected guests"
                    title="Delete selected guests"
                  >
                    {isDeletingSelected ? (
                      <svg viewBox="0 0 24 24" className="h-4 w-4 animate-spin" aria-hidden="true">
                        <circle
                          cx="12"
                          cy="12"
                          r="9"
                          fill="none"
                          stroke="currentColor"
                          strokeOpacity="0.3"
                          strokeWidth="2"
                        />
                        <path
                          d="M21 12a9 9 0 0 0-9-9"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    ) : (
                      <>
                        <svg
                          viewBox="0 0 24 24"
                          className="h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M3 6h18" />
                          <path d="M8 6V4.75A1.75 1.75 0 0 1 9.75 3h4.5A1.75 1.75 0 0 1 16 4.75V6" />
                          <path d="M19 6l-1 13.25A1.75 1.75 0 0 1 16.26 21H7.74A1.75 1.75 0 0 1 6 19.25L5 6" />
                          <path d="M10 10.5v6" />
                          <path d="M14 10.5v6" />
                        </svg>
                        <span className="ml-2 text-xs font-semibold">{selectedVisibleGuestIds.length}</span>
                      </>
                    )}
                  </button>
                </div>
                <div className="hidden sm:flex flex-col items-center gap-1">
                  <span className={filterLabelTextClass}>{downloadAllFilterTitle}</span>
                  <button
                    type="button"
                    onClick={handleDownloadAllGuestsExcel}
                    data-tab-trigger="true"
                    disabled={isDownloadingAllGuests || totalGuestCount === 0}
                    className={`h-10 min-w-10 items-center justify-center rounded-full border px-2 transition ${
                      isDownloadingAllGuests || totalGuestCount === 0
                        ? "cursor-not-allowed border-[color:var(--field-border)] bg-[color:var(--surface)] text-[color:var(--muted)] opacity-60"
                        : "border-[color:var(--field-border)] bg-[color:var(--surface)] text-[color:var(--foreground)] hover:bg-[color:var(--surface-hover)]"
                    }`}
                    aria-label="Download all guests"
                    title="Download all guests"
                  >
                    {isDownloadingAllGuests ? (
                      <svg viewBox="0 0 24 24" className="h-4 w-4 animate-spin" aria-hidden="true">
                        <circle
                          cx="12"
                          cy="12"
                          r="9"
                          fill="none"
                          stroke="currentColor"
                          strokeOpacity="0.3"
                          strokeWidth="2"
                        />
                        <path
                          d="M21 12a9 9 0 0 0-9-9"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    ) : (
                      <svg
                        viewBox="0 0 24 24"
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M12 3v12" />
                        <path d="m7 10 5 5 5-5" />
                        <path d="M4 19h16" />
                      </svg>
                    )}
                  </button>
                </div>
                {showGuestsWithGiftOnly ? (
                  <div className="flex flex-col items-center gap-1">
                    <span className={filterLabelTextClass}>{downloadGiftListLabel}</span>
                    <button
                      type="button"
                      onClick={() => void onDownloadGuestsWithGiftExcel()}
                      data-tab-trigger="true"
                      disabled={isDownloadingGuestsWithGift || visibleAttendanceRows.length === 0}
                      className={`h-10 min-w-10 items-center justify-center rounded-full border px-3 transition ${
                        isDownloadingGuestsWithGift || visibleAttendanceRows.length === 0
                          ? "cursor-not-allowed border-[color:var(--field-border)] bg-[color:var(--surface)] text-[color:var(--muted)] opacity-60"
                          : "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                      }`}
                    >
                      {isDownloadingGuestsWithGift ? (
                        <svg viewBox="0 0 24 24" className="h-4 w-4 animate-spin" aria-hidden="true">
                          <circle
                            cx="12"
                            cy="12"
                            r="9"
                            fill="none"
                            stroke="currentColor"
                            strokeOpacity="0.3"
                            strokeWidth="2"
                          />
                          <path
                            d="M21 12a9 9 0 0 0-9-9"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      ) : (
                        <svg
                          viewBox="0 0 24 24"
                          className="h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M12 3v12" />
                          <path d="m7 10 5 5 5-5" />
                          <path d="M4 19h16" />
                        </svg>
                      )}
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <input
            ref={guestImportInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={handleGuestImportChange}
          />
          {guestImportFeedback ? (
            <p
              className={`mt-3 text-xs ${
                guestImportFeedback.tone === "error"
                  ? "text-red-600"
                  : guestImportFeedback.tone === "warning"
                    ? "text-amber-700"
                    : "text-emerald-700"
              }`}
            >
              {guestImportFeedback.message} <span className="text-[color:var(--muted)]">{uploadExcelHint}</span>
            </p>
          ) : (
            <p className="mt-3 text-xs text-[color:var(--muted)]">{uploadExcelHint}</p>
          )}
        </div>
        <div className="mt-6 min-h-0 flex-1 overflow-hidden rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--surface)]">
          <div className="relative max-h-[76svh] overflow-x-auto overflow-y-auto overscroll-contain pb-6 sm:h-full sm:max-h-none">
          <table className="min-w-full w-full table-fixed text-left text-sm">
            <colgroup>
              <col style={{ width: "4%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "21%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "6%" }} />
              <col style={{ width: "6%" }} />
            </colgroup>
            <thead className="sticky top-0 z-10 bg-[color:var(--surface-strong)] text-xs uppercase tracking-widest text-[color:var(--muted)]">
              <tr>
                <th className="px-2 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={(event) => {
                      if (event.target.checked) {
                        setSelectedGuestIds(paginatedAttendanceRows.map((guest) => guest.id));
                        return;
                      }
                      setSelectedGuestIds([]);
                    }}
                    className="h-4 w-4 rounded border-[color:var(--field-border)]"
                    aria-label="Select all guests"
                  />
                </th>
                <th className="px-4 py-3 text-center font-semibold">{guestsText[language].table.no}</th>
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Category</th>
                <th className="px-4 py-3 font-semibold">Guest group</th>
                <th className="px-4 py-3 font-semibold">Latitude</th>
                <th className="px-4 py-3 font-semibold">Longitude</th>
                <th className="px-4 py-3 font-semibold">Gift amount</th>
                <th className="px-4 py-3 font-semibold">Currency</th>
                <th className="px-4 py-3 font-semibold">Invited</th>
                <th className="px-4 py-3 font-semibold">Done</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--divider)] bg-[color:var(--surface)]">
              {paginatedAttendanceRows.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-6 text-center text-sm text-[color:var(--muted)]">
                    {guestsText[language].noGuests}
                  </td>
                </tr>
              ) : (
                paginatedAttendanceRows.map((guest, index) => {
                  const isDone = guest.done ?? doneById(guest.id);
                  return (
                  <tr key={guest.id} className="transition-colors hover:bg-[color:var(--surface-hover)]/70">
                    <td className="px-2 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={selectedGuestIds.includes(guest.id)}
                        onChange={(event) => {
                          setSelectedGuestIds((prev) =>
                            event.target.checked
                              ? prev.includes(guest.id)
                                ? prev
                                : [...prev, guest.id]
                              : prev.filter((id) => id !== guest.id),
                          );
                        }}
                        className="h-4 w-4 rounded border-[color:var(--field-border)]"
                        aria-label={`Select ${guest.name || `guest ${guest.id}`}`}
                      />
                    </td>
                    <td className="px-4 py-3 text-center text-[color:var(--muted)]">{index + 1}</td>
                    <td className="px-4 py-3 truncate">{guest.name || "-"}</td>
                    <td className="px-4 py-3 truncate">{localizeCategory(guest.category)}</td>
                    <td className="px-4 py-3 truncate">{toApiGuestGroupValue(guest.group)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-[color:var(--muted)]">
                      {guest.latitude != null ? guest.latitude.toFixed(6) : "-"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-[color:var(--muted)]">
                      {guest.longitude != null ? guest.longitude.toFixed(6) : "-"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          data-tab-trigger="true"
                          onClick={() => openGiftModal(guest)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-indigo-200 bg-indigo-50 text-indigo-700 shadow-sm transition duration-150 hover:-translate-y-0.5 hover:border-indigo-300 hover:bg-indigo-100 hover:shadow"
                          aria-label="Set ចំណងដៃ"
                          title="Set ចំណងដៃ"
                        >
                          <svg
                            viewBox="0 0 24 24"
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M3 7h18v10H3z" />
                            <path d="M3 11h18" />
                            <path d="M12 7v10" />
                            <path d="M9.5 7c-1.3 0-2.5-1-2.5-2.3S8 2.5 9.3 2.5C11 2.5 12 4.2 12 7" />
                            <path d="M14.5 7c1.3 0 2.5-1 2.5-2.3S16 2.5 14.7 2.5C13 2.5 12 4.2 12 7" />
                          </svg>
                        </button>
                        <span className="whitespace-nowrap text-xs text-[color:var(--muted)]">
                          {guest.giftAmount != null
                            ? guest.giftCurrency === "Riel"
                              ? `${new Intl.NumberFormat("km-KH").format(guest.giftAmount)} ៛`
                              : formatCurrency(guest.giftAmount)
                            : "0"}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-[color:var(--muted)]">
                      {guest.giftCurrency ?? "-"}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        data-tab-trigger="true"
                        onClick={() => onToggleInvite(guest)}
                        className={`rounded-xl px-3 py-1.5 text-xs font-semibold transition ${
                          guest.invited
                            ? "bg-sky-600 text-white shadow-sm"
                            : "border border-[color:var(--field-border)] bg-[color:var(--surface)] text-[color:var(--foreground)] hover:bg-[color:var(--surface-hover)]"
                        }`}
                      >
                        {guest.invited ? guestsText[language].invitedBtn : guestsText[language].inviteBtn}
                      </button>
                    </td>
                    <td className="px-4 py-3 pr-8">
                      <div className="flex justify-center">
                        <button
                          type="button"
                          data-tab-trigger="true"
                          onClick={() => onToggleDone(guest)}
                          className={`min-w-[4.75rem] whitespace-nowrap rounded-xl px-3 py-1.5 text-xs font-semibold transition ${
                            isDone
                              ? "bg-emerald-600 text-white shadow-sm"
                              : "border border-[color:var(--field-border)] bg-[color:var(--surface)] text-[color:var(--foreground)] hover:bg-[color:var(--surface-hover)]"
                          }`}
                        >
                          {isDone ? guestsText[language].doneBtn : guestsText[language].markDoneBtn}
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
            {isLoadingAttendanceGuests ? (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-[color:var(--surface)]/70">
                <svg viewBox="0 0 24 24" className="h-10 w-10 animate-spin" aria-hidden="true">
                  <circle
                    cx="12"
                    cy="12"
                    r="9"
                    fill="none"
                    stroke="currentColor"
                    strokeOpacity="0.25"
                    strokeWidth="2"
                  />
                  <path
                    d="M21 12a9 9 0 0 0-9-9"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
            ) : null}
          </div>
        </div>
        {!showGuestsWithGiftOnly && attendanceGroupFilter === "All" && attendanceTotalPages > 1 ? (
          <div className="mt-4 flex shrink-0 items-center justify-between gap-3 rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--surface)] px-4 py-3 text-sm text-[color:var(--muted)]">
            <span>{isKh ? `ទំព័រ ${attendancePage} / ${attendanceTotalPages}` : `Page ${attendancePage} / ${attendanceTotalPages}`}</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-tab-trigger="true"
                onClick={() => onAttendancePageChange(Math.max(1, attendancePage - 1))}
                disabled={attendancePage <= 1 || isLoadingAttendanceGuests}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                  attendancePage <= 1 || isLoadingAttendanceGuests
                    ? "cursor-not-allowed bg-[color:var(--field-border)] text-[color:var(--muted)] opacity-70"
                    : "border border-[color:var(--field-border)] bg-[color:var(--surface)] text-[color:var(--foreground)] hover:bg-[color:var(--surface-hover)]"
                }`}
              >
                {isKh ? "មុន" : "Prev"}
              </button>
              <button
                type="button"
                data-tab-trigger="true"
                onClick={() => onAttendancePageChange(Math.min(attendanceTotalPages, attendancePage + 1))}
                disabled={attendancePage >= attendanceTotalPages || isLoadingAttendanceGuests}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                  attendancePage >= attendanceTotalPages || isLoadingAttendanceGuests
                    ? "cursor-not-allowed bg-[color:var(--field-border)] text-[color:var(--muted)] opacity-70"
                    : "border border-[color:var(--field-border)] bg-[color:var(--surface)] text-[color:var(--foreground)] hover:bg-[color:var(--surface-hover)]"
                }`}
              >
                {isKh ? "បន្ទាប់" : "Next"}
              </button>
            </div>
          </div>
        ) : null}
        <div className="mt-4 grid shrink-0 grid-cols-2 gap-3 lg:grid-cols-5">
          <div className="rounded-2xl border border-sky-200/80 bg-sky-50/80 p-3 sm:p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-sky-700">
              {totalGuestsLabel}
            </p>
            <p className="mt-1 text-2xl font-bold text-sky-900">{visibleTotalGuestCount}</p>
          </div>
          <div className="rounded-2xl border border-rose-200/80 bg-rose-50/80 p-3 sm:p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-rose-700">
              {brideGuestTotalLabel}
            </p>
            <p className="mt-1 text-2xl font-bold text-rose-900">{brideGuestCount}</p>
          </div>
          <div className="rounded-2xl border border-indigo-200/80 bg-indigo-50/80 p-3 sm:p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-indigo-700">
              {groomGuestTotalLabel}
            </p>
            <p className="mt-1 text-2xl font-bold text-indigo-900">{groomGuestCount}</p>
          </div>
          <div className="rounded-2xl border border-amber-200/80 bg-amber-50/80 p-3 sm:p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-700">
              {withGiftCountLabel}
            </p>
            <p className="mt-1 text-2xl font-bold text-amber-900">{visibleWithGiftGuestCount}</p>
          </div>
          <div className="col-span-2 rounded-2xl border border-violet-200/80 bg-violet-50/80 p-3 sm:col-span-2 sm:p-4 lg:col-span-1">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-violet-700">
              {totalGiftAmountLabel}
            </p>
            <div className="mt-1 space-y-1">
              <p className="text-base font-bold text-violet-900 sm:text-lg">
                {guestsText[language].totalUsd}: {formatCurrency(visibleAttendanceGiftTotals.usd)}
              </p>
              <p className="text-sm font-semibold text-violet-800">
                {guestsText[language].totalRiel}: {new Intl.NumberFormat("km-KH").format(visibleAttendanceGiftTotals.riel)} ៛
              </p>
            </div>
          </div>
        </div>
      </div>
      {showEstimateModal ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(15, 23, 42, 0.12)" }}
          onClick={() => setShowEstimateModal(false)}
        >
          <div
            className="relative w-full max-w-2xl overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_20px_50px_rgba(0,0,0,0.1)] dark:border-white/10 dark:bg-slate-900 dark:shadow-[0_30px_80px_rgba(15,23,42,0.35)]"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowEstimateModal(false)}
              className="absolute right-5 top-5 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-slate-100 text-slate-500 transition hover:bg-slate-200 dark:border-white/10 dark:bg-white/10 dark:text-slate-300 dark:hover:bg-white/15"
              aria-label={closeLabel}
              title={closeLabel}
            >
              <svg
                viewBox="0 0 24 24"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
            <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-5 dark:border-white/5 dark:bg-white/5 sm:px-7 sm:py-6">
              <h3 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-50 sm:text-2xl">
                {estimateButtonLabel}
              </h3>
            </div>
            <div className="space-y-4 p-6 text-sm sm:p-7">
              {isLoadingGuestEstimate ? (
                <div className="flex min-h-[14rem] items-center justify-center">
                  <p className="text-slate-500">{language === "kh" ? "កំពុងទាញយក..." : "Loading..."}</p>
                </div>
              ) : guestEstimateError ? (
                <div className="rounded-2xl border border-red-200 bg-red-50/80 p-4 text-center text-red-600 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
                  {guestEstimateError}
                </div>
              ) : guestEstimate ? (
                <>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="rounded-[1.5rem] border border-amber-200 bg-amber-50/50 p-6 dark:border-amber-400/20 dark:bg-amber-500/10">
                      <p className="text-5xl font-black tracking-tight text-amber-600 dark:text-amber-400">
                        {estimatedTableRangeText}
                      </p>
                      <p className="mt-1 text-sm font-bold text-amber-900/60 dark:text-amber-100/70">
                        {estimateTableRangeLabel}
                      </p>
                    </div>
                    <div className="rounded-[1.5rem] border border-cyan-200 bg-cyan-50/50 p-6 dark:border-cyan-400/20 dark:bg-cyan-500/10">
                      <p className="text-5xl font-black tracking-tight text-cyan-600 dark:text-cyan-400">
                        {guestEstimate.estimatedGuestTotal}
                      </p>
                      <p className="mt-1 text-sm font-bold text-cyan-900/60 dark:text-cyan-100/70">
                        {estimateCountLabel}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="rounded-2xl border border-rose-200 bg-rose-50/50 p-4 dark:border-rose-400/20 dark:bg-rose-500/10">
                      <p className="text-3xl font-bold text-rose-600 dark:text-rose-400">{guestEstimate.brideTotal}</p>
                      <p className="text-xs font-bold text-rose-900/50 dark:text-rose-100/60">{brideGuestTotalLabel}</p>
                    </div>
                    <div className="rounded-2xl border border-indigo-200 bg-indigo-50/50 p-4 dark:border-indigo-400/20 dark:bg-indigo-500/10">
                      <p className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">{guestEstimate.groomTotal}</p>
                      <p className="text-xs font-bold text-indigo-900/50 dark:text-indigo-100/60">{groomGuestTotalLabel}</p>
                    </div>
                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-4 dark:border-emerald-400/20 dark:bg-emerald-500/10">
                      <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">{guestEstimate.totalFound}</p>
                      <p className="text-xs font-bold text-emerald-900/50 dark:text-emerald-100/60">{totalGuestsLabel}</p>
                    </div>
                  </div>
                  <div className="grid gap-3 rounded-[1.5rem] border border-slate-100 bg-slate-50/50 p-4 dark:border-white/10 dark:bg-white/5 sm:grid-cols-2">
                    <div className="flex items-center justify-between rounded-xl border border-violet-100 bg-white px-4 py-3 dark:border-violet-400/20 dark:bg-violet-500/10">
                      <span className="font-medium text-slate-500 dark:text-violet-200/70">
                        {language === "kh" ? "ភ្ញៀវក្នុងមួយតុ" : "Guests per table"}
                      </span>
                      <span className="text-lg font-bold text-violet-700 dark:text-violet-300">{guestEstimate.guestsPerTable}</span>
                    </div>
                    <div className="flex items-center justify-between rounded-xl border border-fuchsia-100 bg-white px-4 py-3 dark:border-fuchsia-400/20 dark:bg-fuchsia-500/10">
                      <span className="font-medium text-slate-500 dark:text-fuchsia-200/70">
                        {language === "kh" ? "ចំនួនតុសរុប" : "Total tables"}
                      </span>
                      <span className="text-lg font-bold text-fuchsia-700 dark:text-fuchsia-300">{guestEstimate.estimatedTableCount}</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="rounded-[1.5rem] border border-dashed border-slate-300 p-8 text-center dark:border-white/15">
                  <p className="text-slate-500 dark:text-slate-400">
                    {language === "kh" ? "មិនទាន់មានទិន្នន័យប៉ាន់ស្មានទេ" : "No estimate yet."}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
      {showGuestImportModal ? (
        <div
          className="fixed inset-0 z-50 flex items-stretch justify-center px-2 pb-6 pt-2 sm:px-4 sm:pb-10 sm:pt-4"
          style={{ background: "var(--modal-backdrop)" }}
          onClick={() => {
            if (isUploadingGuestExcel) return;
            setShowGuestImportModal(false);
          }}
        >
          <div
            className="flex h-full w-full max-w-7xl flex-col overflow-hidden rounded-3xl border border-[color:var(--field-border)] bg-[color:var(--surface)] shadow-[var(--modal-shadow)]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 border-b border-[color:var(--field-border)] px-5 py-4 sm:px-6">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-[color:var(--foreground)]">
                  {uploadGuestModalTitle}
                </h3>
                <p className="mt-1 text-sm text-[color:var(--muted)]">{uploadExcelHint}</p>
              </div>
              <button
                type="button"
                data-tab-trigger="true"
                onClick={() => setShowGuestImportModal(false)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[color:var(--field-border)] bg-[color:var(--surface)] text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
                aria-label={closeLabel}
                title={closeLabel}
              >
                <svg
                  viewBox="0 0 24 24"
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            </div>
            <div className="flex min-h-0 flex-1 flex-col gap-4 p-4 sm:p-6">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="rounded-2xl border border-dashed border-[color:var(--field-border)] bg-[color:var(--surface-strong)]/40 p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-medium text-[color:var(--foreground)]">
                        {guestImportFile?.name ?? noFileSelectedLabel}
                      </p>
                      <p className="mt-1 text-xs text-[color:var(--muted)]">
                        {isKh
                          ? "Upload .xlsx, .xls, or .csv. Gift អាចទទេបាន។"
                          : "Upload .xlsx, .xls, or .csv. Gift can be empty."}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        data-tab-trigger="true"
                        onClick={onDownloadGuestImportSample}
                        className="inline-flex h-10 items-center justify-center rounded-full border border-[color:var(--field-border)] bg-[color:var(--surface)] px-4 text-sm font-semibold text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
                      >
                        {downloadSampleLabel}
                      </button>
                      <button
                        type="button"
                        data-tab-trigger="true"
                        onClick={() => guestImportInputRef.current?.click()}
                        className="inline-flex h-10 items-center justify-center rounded-full bg-[color:var(--accent)] px-4 text-sm font-semibold text-white transition hover:brightness-110"
                      >
                        {chooseFileLabel}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-2 lg:min-w-[18rem]">
                  <div className="text-sm font-semibold text-[color:var(--foreground)]">
                    {isKh
                      ? `តារាង Preview (${filteredGuestImportPreviewRows.length}/${guestImportPreviewRows.length})`
                      : `Preview Table (${filteredGuestImportPreviewRows.length}/${guestImportPreviewRows.length})`}
                  </div>
                  <input
                    value={guestImportPreviewSearch}
                    onChange={(event) => setGuestImportPreviewSearch(event.target.value)}
                    placeholder={isKh ? "ស្វែងរកក្នុងតារាង preview..." : "Search preview table..."}
                    className="h-10 w-full rounded-full border border-[color:var(--field-border)] bg-[color:var(--surface)] px-4 text-sm text-[color:var(--foreground)] outline-none transition focus:border-[color:var(--accent)]"
                  />
                </div>
              </div>
              <div className="min-h-0 flex-1 overflow-hidden rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--surface)]">
                {isLoadingGuestImportPreview ? (
                  <div className="flex h-full items-center justify-center px-4 py-6 text-center text-sm text-[color:var(--muted)]">
                    {isKh ? "កំពុងបង្ហាញទិន្នន័យមុនអាប់ឡូត..." : "Loading preview..."}
                  </div>
                ) : guestImportPreviewRows.length > 0 ? (
                  <div className="h-full overflow-auto">
                    <table className="min-w-full text-left text-sm">
                      <thead className="sticky top-0 z-10 bg-[color:var(--surface-strong)] text-[color:var(--muted)]">
                        <tr>
                          <th className="px-4 py-3">#</th>
                          <th className="px-4 py-3">{isKh ? "ឈ្មោះភ្ញៀវ" : "Guest name"}</th>
                          <th className="px-4 py-3">{isKh ? "ក្រុមភ្ញៀវ" : "Group guest"}</th>
                          <th className="px-4 py-3">{isKh ? "ចំនួនចំណងដៃ" : "Gift amount"}</th>
                          <th className="px-4 py-3">{isKh ? "រូបិយប័ណ្ណ" : "Currency"}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[color:var(--divider)]">
                        {filteredGuestImportPreviewRows.map((row, index) => (
                          <tr key={`${row.name}-${index}`} className="align-top">
                            <td className="px-4 py-3 text-[color:var(--muted)]">{index + 1}</td>
                            <td className="px-4 py-3 text-[color:var(--foreground)]">{row.name}</td>
                            <td className="px-4 py-3 text-[color:var(--foreground)]">
                              {row.group == null
                                ? "-"
                                : row.group === "ភ្ញៀវកូនស្រី"
                                ? guestsText[language].groupFilter.bride
                                : guestsText[language].groupFilter.groom}
                            </td>
                            <td className="px-4 py-3 text-[color:var(--foreground)]">
                              {row.hasGift ? row.giftAmount ?? 0 : "-"}
                            </td>
                            <td className="px-4 py-3 text-[color:var(--foreground)]">
                              {row.hasGift ? row.giftCurrency : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : guestImportPreviewSearch.trim() ? (
                  <div className="flex h-full items-center justify-center px-4 py-6 text-center text-sm text-[color:var(--muted)]">
                    {isKh ? "មិនមានជួរដែលត្រូវនឹងការស្វែងរកទេ។" : "No preview rows match your search."}
                  </div>
                ) : (
                  <div className="flex h-full items-center justify-center px-4 py-6 text-center text-sm text-[color:var(--muted)]">
                    {guestImportFile
                      ? isKh
                        ? "មិនមានទិន្នន័យសម្រាប់បង្ហាញ preview ទេ។"
                        : "No preview rows available."
                      : isKh
                        ? "សូមជ្រើសឯកសារ ដើម្បីបង្ហាញតារាង preview។"
                        : "Choose a file to display the preview table."}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-[color:var(--field-border)] px-5 py-4 sm:px-6">
              <button
                type="button"
                data-tab-trigger="true"
                onClick={() => setShowGuestImportModal(false)}
                className="rounded-full border border-[color:var(--field-border)] bg-[color:var(--surface)] px-4 py-2 text-sm font-semibold text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
              >
                {closeLabel}
              </button>
              <button
                type="button"
                data-tab-trigger="true"
                onClick={handleGuestImportSubmit}
                disabled={
                  isUploadingGuestExcel ||
                  isLoadingGuestImportPreview ||
                  !guestImportFile
                }
                className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                  isUploadingGuestExcel ||
                  isLoadingGuestImportPreview ||
                  !guestImportFile
                    ? "cursor-not-allowed bg-[color:var(--field-border)] text-[color:var(--muted)] opacity-70"
                    : "bg-[color:var(--accent)] !text-white hover:brightness-110 dark:!text-white"
                }`}
              >
                {isUploadingGuestExcel ? (isKh ? "កំពុងអាប់ឡូត..." : "Uploading...") : uploadLabel}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {showCategoryModal ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "var(--modal-backdrop)" }}
          onClick={() => {
            if (isCategorySaving) return;
            setShowCategoryModal(false);
          }}
        >
          <div
            className="w-full max-w-xl rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--surface)] p-5 shadow-[var(--modal-shadow)]"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-[color:var(--foreground)]">
              {isKh ? "គ្រប់គ្រងប្រភេទភ្ញៀវ" : "Manage categories"}
            </h3>
            <div className="mt-4 flex gap-2">
              <input
                value={newCategoryNameEn}
                onChange={(event) => setNewCategoryNameEn(event.target.value)}
                placeholder={isKh ? "ឈ្មោះអង់គ្លេស (ត្រូវបំពេញ)" : "English name (required)"}
                className="h-10 flex-1 rounded-xl border border-[color:var(--field-border)] bg-[color:var(--surface)] px-3 text-sm text-[color:var(--foreground)] outline-none focus:border-[color:var(--accent)]"
              />
              <input
                value={newCategoryNameKh}
                onChange={(event) => setNewCategoryNameKh(event.target.value)}
                placeholder={isKh ? "ឈ្មោះខ្មែរ (ជម្រើស)" : "Khmer name (optional)"}
                className="h-10 flex-1 rounded-xl border border-[color:var(--field-border)] bg-[color:var(--surface)] px-3 text-sm text-[color:var(--foreground)] outline-none focus:border-[color:var(--accent)]"
              />
              <button
                type="button"
                data-tab-trigger="true"
                disabled={isCategorySaving || !newCategoryNameEn.trim()}
                onClick={async () => {
                  const nameEn = newCategoryNameEn.trim();
                  const nameKh = newCategoryNameKh.trim();
                  if (!nameEn) return;
                  if (!isEnglishCategoryName(nameEn)) {
                    setCategoryError(
                      isKh
                        ? "សូមបញ្ចូលឈ្មោះប្រភេទជាអក្សរអង់គ្លេស។"
                        : "Category name must be in English.",
                    );
                    return;
                  }
                  setIsCategorySaving(true);
                  setCategoryError("");
                  try {
                    await onCreateCategory(nameEn, nameKh || undefined);
                    setNewCategoryNameEn("");
                    setNewCategoryNameKh("");
                  } catch (error) {
                    setCategoryError(error instanceof Error ? error.message : "Unable to create category.");
                  } finally {
                    setIsCategorySaving(false);
                  }
                }}
                className={`rounded-xl px-4 text-xs font-semibold uppercase tracking-widest transition ${
                  isCategorySaving || !newCategoryNameEn.trim()
                    ? "cursor-not-allowed bg-[color:var(--field-border)] text-[color:var(--muted)] opacity-70"
                    : "bg-[color:var(--accent)] !text-white hover:brightness-110 dark:!text-white"
                }`}
              >
                {isKh ? "បន្ថែម" : "Add"}
              </button>
            </div>
            {categoryError ? (
              <p className="mt-2 text-sm text-red-600">{categoryError}</p>
            ) : null}
            <div className="mt-4 max-h-[22rem] overflow-auto rounded-xl border border-[color:var(--field-border)]">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 bg-[color:var(--surface-strong)] text-xs uppercase tracking-widest text-[color:var(--muted)]">
                  <tr>
                    <th className="px-3 py-2">ID</th>
                    <th className="px-3 py-2">{isKh ? "ឈ្មោះ" : "Name"}</th>
                    <th className="px-3 py-2 text-right">{guestsText[language].table.action}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[color:var(--divider)]">
                  {categoryItems.map((item) => (
                    <tr key={item.id}>
                      <td className="px-3 py-2 text-[color:var(--muted)]">{item.id}</td>
                      <td className="px-3 py-2">
                        {editingCategoryId === item.id ? (
                          <div className="space-y-2">
                            <input
                              value={editingCategoryNameEn}
                              onChange={(event) => setEditingCategoryNameEn(event.target.value)}
                              placeholder={isKh ? "ឈ្មោះអង់គ្លេស" : "English name"}
                              className="h-8 w-full rounded-lg border border-[color:var(--field-border)] bg-[color:var(--surface)] px-2 text-sm text-[color:var(--foreground)] outline-none focus:border-[color:var(--accent)]"
                            />
                            <input
                              value={editingCategoryNameKh}
                              onChange={(event) => setEditingCategoryNameKh(event.target.value)}
                              placeholder={isKh ? "ឈ្មោះខ្មែរ (ជម្រើស)" : "Khmer name (optional)"}
                              className="h-8 w-full rounded-lg border border-[color:var(--field-border)] bg-[color:var(--surface)] px-2 text-sm text-[color:var(--foreground)] outline-none focus:border-[color:var(--accent)]"
                            />
                          </div>
                        ) : (
                          <span>{item.name}</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-2">
                          {editingCategoryId === item.id ? (
                            <>
                              <button
                                type="button"
                                data-tab-trigger="true"
                                disabled={isCategorySaving || !editingCategoryNameEn.trim()}
                                onClick={async () => {
                                  const nameEn = editingCategoryNameEn.trim();
                                  const nameKh = editingCategoryNameKh.trim();
                                  if (!nameEn) return;
                                  if (!isEnglishCategoryName(nameEn)) {
                                    setCategoryError(
                                      isKh
                                        ? "សូមបញ្ចូលឈ្មោះប្រភេទជាអក្សរអង់គ្លេស។"
                                        : "Category name must be in English.",
                                    );
                                    return;
                                  }
                                  setIsCategorySaving(true);
                                  setCategoryError("");
                                  try {
                                    await onUpdateCategory(item.id, nameEn, nameKh || undefined);
                                    setEditingCategoryId(null);
                                    setEditingCategoryNameEn("");
                                    setEditingCategoryNameKh("");
                                  } catch (error) {
                                    setCategoryError(
                                      error instanceof Error ? error.message : "Unable to update category.",
                                    );
                                  } finally {
                                    setIsCategorySaving(false);
                                  }
                                }}
                                className="rounded-lg bg-emerald-600 px-2 py-1 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                {isKh ? "រក្សាទុក" : "Save"}
                              </button>
                              <button
                                type="button"
                                data-tab-trigger="true"
                                disabled={isCategorySaving}
                                onClick={() => {
                                  setEditingCategoryId(null);
                                  setEditingCategoryNameEn("");
                                  setEditingCategoryNameKh("");
                                }}
                                className="rounded-lg border border-[color:var(--field-border)] px-2 py-1 text-xs font-semibold text-[color:var(--foreground)]"
                              >
                                {isKh ? "បោះបង់" : "Cancel"}
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                data-tab-trigger="true"
                                disabled={isCategorySaving}
                                onClick={() => {
                                  setEditingCategoryId(item.id);
                                  setEditingCategoryNameEn(item.name);
                                  setEditingCategoryNameKh(
                                    categoryTranslations[item.name.trim().toLowerCase()] ?? "",
                                  );
                                  setCategoryError("");
                                }}
                                className="rounded-lg border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-700"
                              >
                                {isKh ? "កែ" : "Edit"}
                              </button>
                              <button
                                type="button"
                                data-tab-trigger="true"
                                disabled={isCategorySaving}
                                onClick={async () => {
                                  setIsCategorySaving(true);
                                  setCategoryError("");
                                  try {
                                    await onDeleteCategory(item.id);
                                  } catch (error) {
                                    setCategoryError(
                                      error instanceof Error ? error.message : "Unable to delete category.",
                                    );
                                  } finally {
                                    setIsCategorySaving(false);
                                  }
                                }}
                                className="rounded-lg bg-red-600 px-2 py-1 text-xs font-semibold text-white"
                              >
                                {isKh ? "លុប" : "Delete"}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                data-tab-trigger="true"
                onClick={() => setShowCategoryModal(false)}
                className="rounded-full bg-[color:var(--accent)] px-4 py-2 text-xs font-semibold uppercase tracking-widest text-white transition hover:brightness-110"
              >
                {closeLabel}
              </button>
            </div>
            
          </div>
        </div>
      ) : null}
    </div>
  );
}
